import { useStore } from '../store';
import { HeadwindUserSettings } from '../types';
import { isSharedOrEmbeddedRide, oneDecPlace } from './utils';

export const degToCompass = (n: number) => {
  var val = Math.floor(n / 22.5 + 0.5);
  var arr = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return arr[val % 16];
};

export const kmToMiles = (km: number) => {
  return km / 1.609;
};

export const milesToKm = (miles: number) => {
  return miles * 1.609;
};

export const metersToFeet = (meters: number) => {
  return meters * 3.28084;
};

export const centigradeToFahrenheit = (c: number) => {
  return (c * 9) / 5 + 32;
};

export const mmToInches = (mm: number) => {
  return mm / 25.4;
};

export const metersToKm = (m: number) => {
  return m / 1000;
};

export const kmToMeters = (km: number) => {
  return km * 1000;
};

export const metersPerSecondToKph = (mps: number) => {
  return mps * 3.6;
};

export const kphToMetersPerSecond = (kph: number) => {
  return kph / 3.6;
};

export const kjToCal = (kj: number) => {
  return Math.round(kj / 4.184);
};

export const radToDeg = (rad: number) => {
  return rad * (180 / Math.PI);
};

export const getUserPrefUnit = (type: keyof HeadwindUserSettings['unitPrefs'], full = false) => {
  const { store } = useStore();
  let settings = store().settings.unitPrefs;
  if (isSharedOrEmbeddedRide()) {
    settings = store().sharedUnitPrefs;
  }
  if (settings && settings[type]) {
    if (full) {
      return settings[type];
    }
    switch (settings[type]) {
      case 'kilometers':
        return 'km';
      case 'miles':
        return 'mi';
      case 'millimeters':
        return 'mm';
      case 'feet':
        return "'";
      case 'meters':
        return 'm';
      default:
        return settings[type];
    }
  }
  return type;
};

export const convertToUserPref = (number: number, type: keyof HeadwindUserSettings['unitPrefs']) => {
  const { store } = useStore();
  let settings = store().settings.unitPrefs;
  if (isSharedOrEmbeddedRide()) {
    settings = store().sharedUnitPrefs;
  }
  if (!settings) {
    return oneDecPlace(number);
  }
  let value = number;
  if (settings && settings[type]) {
    if (settings[type] === 'miles' || settings[type] === 'MPH') {
      value = kmToMiles(number);
    } else if (settings[type] === 'inches') {
      value = mmToInches(number);
    } else if (settings[type] === 'fahrenheit') {
      value = centigradeToFahrenheit(number);
    } else if (settings[type] === 'feet') {
      value = metersToFeet(number);
    }
  }
  return oneDecPlace(value);
};

// Inverse of convertToUserPref, needed when a value entered/displayed in the user's
// preferred unit (eg. MPH) has to be converted back to the metric units used internally.
export const convertFromUserPref = (number: number, type: keyof HeadwindUserSettings['unitPrefs']) => {
  const { store } = useStore();
  let settings = store().settings.unitPrefs;
  if (isSharedOrEmbeddedRide()) {
    settings = store().sharedUnitPrefs;
  }
  if (!settings) {
    return number;
  }
  let value = number;
  if (settings && settings[type]) {
    if (settings[type] === 'miles' || settings[type] === 'MPH') {
      value = milesToKm(number);
    } else if (settings[type] === 'inches') {
      value = number * 25.4;
    } else if (settings[type] === 'fahrenheit') {
      value = ((number - 32) * 5) / 9;
    } else if (settings[type] === 'feet') {
      value = number / 3.28084;
    }
  }
  return value;
};
